import { addBreadcrumb, captureMessage } from './sentry';

/**
 * Analytics event types for tracking user actions
 * These are the key user journey events we want to monitor
 */
export const AnalyticsEvent = {
  // Link card interactions
  LINK_CARD_CLICK: 'link_card.click',
  LINK_CARD_FAVORITE: 'link_card.favorite',
  LINK_CARD_ARCHIVE: 'link_card.archive',
  LINK_CARD_EXTERNAL: 'link_card.external_link',

  // Link detail page
  LINK_DETAIL_LOADED: 'link_detail.loaded',
  LINK_DETAIL_LOAD_FAILED: 'link_detail.load_failed',
  LINK_DETAIL_NOT_FOUND: 'link_detail.not_found',
  LINK_DETAIL_ACTION: 'link_detail.action',

  // Add link flow
  ADD_LINK_DIALOG_OPEN: 'add_link.dialog_open',
  ADD_LINK_SUBMITTED: 'add_link.submitted',
  ADD_LINK_SUCCESS: 'add_link.success',
  ADD_LINK_FAILED: 'add_link.failed',

  // Navigation
  PAGE_VIEW: 'page.view',
  FILTER_CHANGED: 'filter.changed',

  // Auth
  LOGIN_SUCCESS: 'auth.login_success',
  LOGIN_FAILED: 'auth.login_failed',
  LOGOUT: 'auth.logout',
} as const;

export type AnalyticsEventType = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

/**
 * Track a user action/event
 *
 * This function:
 * 1. Adds a Sentry breadcrumb (for error context)
 * 2. Logs to console in development
 * 3. Can be extended to send to analytics service (e.g., PostHog, Mixpanel)
 */
export function trackEvent(event: AnalyticsEventType, properties?: Record<string, unknown>) {
  // Always add as breadcrumb for error context
  addBreadcrumb(event, 'ui', properties);

  // Log in development for debugging
  if (import.meta.env.DEV) {
    console.log(`[Analytics] ${event}`, properties || '');
  }

  // Future: Send to analytics service
  // if (window.posthog) {
  //   window.posthog.capture(event, properties);
  // }
}

/**
 * Track a user action that we specifically want to monitor for reliability
 * Use this for critical user journey steps where we want alerts on failure
 */
export function trackCriticalAction(
  action: string,
  success: boolean,
  properties?: Record<string, unknown>
) {
  const eventName = success ? `${action}.success` : `${action}.failed`;

  trackEvent(eventName as AnalyticsEventType, {
    ...properties,
    success,
    timestamp: Date.now(),
  });

  // For failed critical actions, also send to Sentry for monitoring
  if (!success) {
    captureMessage(`Critical action failed: ${action}`, 'warning', properties);
  }
}

/**
 * Measure the duration of an operation
 * Returns a function to call when the operation completes
 */
export function startTiming(
  operationName: string
): (success: boolean, properties?: Record<string, unknown>) => void {
  const startTime = performance.now();

  return (success: boolean, properties?: Record<string, unknown>) => {
    const duration = Math.round(performance.now() - startTime);

    trackEvent(`${operationName}.${success ? 'success' : 'failed'}` as AnalyticsEventType, {
      ...properties,
      durationMs: duration,
    });

    // Log slow operations
    if (success && duration > 3000) {
      captureMessage(`Slow operation: ${operationName}`, 'warning', {
        durationMs: duration,
        ...properties,
      });
    }
  };
}

/**
 * Track link card click with navigation timing
 * This is the main entry point for link card click reliability tracking
 */
export function trackLinkCardClick(linkId: number, linkTitle: string | null) {
  const clickTime = Date.now();

  trackEvent(AnalyticsEvent.LINK_CARD_CLICK, {
    linkId,
    linkTitle: linkTitle?.substring(0, 50), // Truncate for privacy
    clickTime,
  });

  // Return a function to call when the detail page loads
  return {
    onDetailLoaded: (loadSuccess: boolean, error?: string) => {
      const loadTime = Date.now() - clickTime;

      if (loadSuccess) {
        trackEvent(AnalyticsEvent.LINK_DETAIL_LOADED, {
          linkId,
          loadTimeMs: loadTime,
        });
      } else {
        trackEvent(AnalyticsEvent.LINK_DETAIL_LOAD_FAILED, {
          linkId,
          loadTimeMs: loadTime,
          error,
        });
      }
    },
    onNotFound: () => {
      trackEvent(AnalyticsEvent.LINK_DETAIL_NOT_FOUND, {
        linkId,
        timeToNotFound: Date.now() - clickTime,
      });
    },
  };
}
